import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useAppRegion,
  useCurrentUser,
  useEnabledFeatures,
} from '@/hooks/dataHelpers/useCurrentUser';
import { LEGACY_MAC_BUILDS } from '@/lib/offline/OfflineAppHelper';
import { AltArrowDownLinear } from '@assessprep/icons/solar';
import { useTranslate } from '@refinedev/core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ChromebookTab } from './ChromebookTab';
import { IpadTab } from './IpadTab';
import { LegacyMacTab } from './LegacyMacTab';
import { MacTab } from './MacTab';
import { OldAppUsersExportDialog } from './OldAppUsersExportDialog';
import { WindowsTab } from './WindowsTab';

/**
 * APL-11850 (Slice 6) — v4 rebuild of `src/lib/offline/OfflineApp.tsx`.
 * Data layer (OfflineAppHelper, export mutation) is shared with v1, not
 * forked. Platform tab labels are product names — untranslated in v1 too.
 */
export const DownloadAppsPage = () => {
  const translate = useTranslate();
  const appRegion = useAppRegion();
  const currentUser = useCurrentUser();
  const enabledFeatures = useEnabledFeatures();
  const navigate = useNavigate();

  const accommodations = currentUser?.custom_fields?.accommodations ?? {};
  const hasAnyLegacyMacBuild = LEGACY_MAC_BUILDS.some(
    (build) => accommodations[build.accommodationKey]
  );

  const showDiagnosticToolButton =
    enabledFeatures.allow_diagnostic_tool && ['teacher', 'admin'].includes(currentUser?.role ?? '');

  // APL-10884 — visibility gated on the `export_old_app_users` org flag + admin role.
  const showAdminActions = currentUser?.role === 'admin' && !!enabledFeatures.export_old_app_users;

  const showChromebookTab = currentUser?.role === 'admin' && appRegion === 'global';
  const showLegacyMacTab = currentUser?.role === 'student' && hasAnyLegacyMacBuild;

  const [showOldAppUsersModal, setShowOldAppUsersModal] = useState(false);

  return (
    <div className="w-full max-w-4xl space-y-6" data-testid="download-apps-page">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="download-apps-title">
            {translate('CommonText.download_apps')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {translate('OfflineApp.download_assessprep_desktop_apps_for_windows_and_mac_our_apps')}
          </p>
        </div>
        {showAdminActions && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="download-apps-actions">
                {translate('CommonText.actions')} <AltArrowDownLinear aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setShowOldAppUsersModal(true)}
                data-testid="download-old-app-users-item"
              >
                {translate('OfflineApp.download_old_app_users')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Card data-testid="download-apps-card">
        <CardContent>
          <Tabs defaultValue="mac" data-testid="download-apps-tabs">
            <TabsList>
              <TabsTrigger value="mac">Mac</TabsTrigger>
              <TabsTrigger value="windows">Windows</TabsTrigger>
              <TabsTrigger value="ios">iPad</TabsTrigger>
              {showChromebookTab && <TabsTrigger value="cros">Chromebook</TabsTrigger>}
              {showLegacyMacTab && (
                <TabsTrigger value="legacy-app">
                  {translate('OfflineApp.offline_legacy_app')}
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="mac" className="pt-4">
              <MacTab />
            </TabsContent>
            <TabsContent value="windows" className="pt-4">
              <WindowsTab />
            </TabsContent>
            <TabsContent value="ios" className="pt-4">
              <IpadTab />
            </TabsContent>
            {showChromebookTab && (
              <TabsContent value="cros" className="pt-4">
                <ChromebookTab />
              </TabsContent>
            )}
            {showLegacyMacTab && (
              <TabsContent value="legacy-app" className="pt-4">
                <LegacyMacTab accommodations={accommodations} />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {showDiagnosticToolButton && (
        <Card data-testid="diagnostic-tool-card">
          <CardHeader>
            <CardTitle className="text-base">
              {translate('OfflineApp.network_diagnostic_tool')}
            </CardTitle>
            <CardDescription>
              {translate(
                'OfflineApp.use_this_tool_to_diagnose_network_connectivity_issues_and_test_your_connection_to_assessprep_services'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={() => navigate('/diagnostic-tools')}
              data-testid="open-diagnostic-tool"
            >
              {translate('OfflineApp.open_diagnostic_tool')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modal lives at page root so the dropdown item can open it. */}
      {showAdminActions && (
        <OldAppUsersExportDialog
          open={showOldAppUsersModal}
          onOpenChange={setShowOldAppUsersModal}
        />
      )}
    </div>
  );
};
